import React, { useMemo, useCallback, useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { typography, borders, textColors } from "../../../design-system/tokens";
import { getElementTypeLabel } from "../../utils/internalElementIcons";
import { ELEMENT_ICONS } from "../addelements/elementIcons";
import { EditContext } from "../../context/EditContext";
import { useAddElement } from "../../context/AddElementContext";
import {
  EditIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "../../../design-system/icons";
import { apiFetch } from "../../../core/utils/api";

const ITEM_TYPE_LABELS = {
  image: "Image",
  video: "Video",
  title: "Title",
  text: "Text",
  button: "Button",
  button_collection: "Button Collection",
  file: "File",
};

function typeLabel(type) {
  return ITEM_TYPE_LABELS[type] || getElementTypeLabel(type) || type;
}

function TypeIcon({ type, className = "w-3.5 h-3.5" }) {
  const Icon = ELEMENT_ICONS[type];
  if (!Icon) {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    );
  }
  return <Icon className={className} />;
}

function TypeBadge({ type }) {
  return (
    <div
      className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center
                 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200"
    >
      <TypeIcon type={type} />
    </div>
  );
}

function ItemBadge({ item }) {
  const [thumbErrored, setThumbErrored] = useState(false);

  const previewUrl = useMemo(() => {
    if (!item || item.type !== "image" || thumbErrored) return null;
    return item.cropped_image_url || item.image_url || null;
  }, [item, thumbErrored]);

  if (previewUrl) {
    return (
      <div className="flex-shrink-0 w-6 h-6 rounded overflow-hidden bg-gray-100 dark:bg-dark-700">
        <img
          src={previewUrl}
          alt={item?.alt_text || ""}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setThumbErrored(true)}
        />
      </div>
    );
  }

  return <TypeBadge type={item?.type} />;
}

// ---------------------------------------------------------------------------
// Single item card within a bucket
// ---------------------------------------------------------------------------

function BucketItemCard({
  item,
  componentId,
  slotLabel,
  sublabel,
  onMoveUp,
  onMoveDown,
  reordering = false,
}) {
  const editContext = useContext(EditContext);
  const selectElement = editContext?.selectElement;
  const deleteElement = editContext?.deleteElement;
  const clearSelection = editContext?.clearSelection;
  const queryClient = useQueryClient();

  const assignedElement = useMemo(() => {
    if (!item) return null;
    return { ...item, component: componentId };
  }, [item, componentId]);

  const handleSelect = useCallback(() => {
    if (!selectElement || !assignedElement) return;
    selectElement(assignedElement);
  }, [selectElement, assignedElement]);

  const handleCardClick = useCallback(
    (e) => {
      e.stopPropagation();
      handleSelect();
    },
    [handleSelect],
  );

  const handleCardKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        handleSelect();
      }
    },
    [handleSelect],
  );

  const handleEditClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSelect();
    },
    [handleSelect],
  );

  const handleDeleteClick = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!deleteElement || !assignedElement) return;
      await deleteElement(assignedElement);
      if (!selectElement || componentId == null) return;

      // Re-select the PARENT component so the Slot Assignments panel stays open.
      // deleteElement() clears the selection, and re-selecting a bare {id,type}
      // would strip active_component_mode_definition / automatic_layout_buckets,
      // causing the slot manager to hide until the user clicked the component
      // again. Re-fetch the hydrated component first so the panel persists.
      try {
        const hydrated = await queryClient.fetchQuery({
          queryKey: ["component", componentId],
          queryFn: async () => {
            const res = await apiFetch(`/api/components/${componentId}/`, {
              credentials: "include",
            });
            if (!res.ok) {
              throw new Error(`Failed to fetch component ${componentId}`);
            }
            return res.json();
          },
        });
        if (hydrated) {
          selectElement(hydrated);
          return;
        }
      } catch (err) {
        // The component may have been cascade-deleted (last child removed).
        // Don't re-select a dead component; just clear the panel.
        if (clearSelection) {
          clearSelection();
          return;
        }
      }

      selectElement({ id: componentId, type: "component" });
    },
    [deleteElement, assignedElement, selectElement, clearSelection, componentId, queryClient],
  );

  const itemLabel = item?.label || item?.name || getElementTypeLabel(item?.type);

  const actionButtonClass = `flex-shrink-0 flex items-center justify-center w-6 h-6 rounded
                             text-gray-500 dark:text-dark-500
                             hover:text-gray-900 dark:hover:text-dark-100
                             hover:bg-gray-200/70 dark:hover:bg-dark-700
                             focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500
                             transition-colors`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`Edit ${typeLabel(item?.type)}: ${itemLabel}`}
      className={`group w-full rounded-lg border ${borders.default} bg-white/50 dark:bg-dark-800/50
                 flex items-center gap-3 py-2.5 px-3
                 cursor-pointer
                 hover:border-gray-400/70 dark:hover:border-gray-500/70
                 focus:outline-none focus:ring-2 focus:ring-blue-400/50
                 transition-colors`}
    >
      <ItemBadge item={item} />
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className={`text-xs font-medium ${textColors.primary} truncate w-full`}>
          {slotLabel}
        </span>
        <span className={`text-[10px] leading-tight ${textColors.muted} truncate w-full`}>
          {sublabel || itemLabel}
        </span>
      </div>
      <div className="flex-shrink-0 flex items-center gap-0.5">
        {(onMoveUp || onMoveDown) && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onMoveUp && !reordering) onMoveUp();
              }}
              disabled={!onMoveUp || reordering}
              aria-label={`Move ${typeLabel(item?.type)} up`}
              title={onMoveUp ? "Move up" : "Already at the top"}
              className={`${actionButtonClass} ${
                !onMoveUp || reordering ? "opacity-30 cursor-not-allowed" : ""
              }`}
            >
              <ChevronUpIcon size="sm" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onMoveDown && !reordering) onMoveDown();
              }}
              disabled={!onMoveDown || reordering}
              aria-label={`Move ${typeLabel(item?.type)} down`}
              title={onMoveDown ? "Move down" : "Already at the bottom"}
              className={`${actionButtonClass} ${
                !onMoveDown || reordering ? "opacity-30 cursor-not-allowed" : ""
              }`}
            >
              <ChevronDownIcon size="sm" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={handleEditClick}
          aria-label={`Edit ${typeLabel(item?.type)}`}
          title={`Edit ${typeLabel(item?.type)}`}
          className={actionButtonClass}
        >
          <EditIcon size="sm" />
        </button>
        <button
          type="button"
          onClick={handleDeleteClick}
          aria-label={`Delete ${typeLabel(item?.type)}`}
          title={`Delete ${typeLabel(item?.type)}`}
          className={actionButtonClass}
        >
          <TrashIcon size="sm" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flat ordered list — renders all assigned items in their actual order,
// enabling cross-type reordering (critical for modes like Alternating
// where the global order determines image↔content pairing).
// ---------------------------------------------------------------------------

function FlatItemList({ items, componentId, onSwapItems, reordering }) {
  const count = items.length;
  if (count === 0) return null;

  return (
    <div className="space-y-1.5">
      {items.map((item, index) => {
        const onMoveUp =
          index > 0
            ? () => onSwapItems(items[index], items[index - 1])
            : undefined;
        const onMoveDown =
          index < count - 1
            ? () => onSwapItems(items[index], items[index + 1])
            : undefined;

        const itemLabel = item?.label || item?.name || getElementTypeLabel(item?.type);

        return (
          <BucketItemCard
            key={`flat-${item?.type}-${item?.id}`}
            item={item}
            componentId={componentId}
            slotLabel={typeLabel(item?.type)}
            sublabel={itemLabel}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            reordering={reordering}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unassigned item card (hidden items)
// ---------------------------------------------------------------------------

function UnassignedItemCard({ item }) {
  return (
    <div
      className={`w-full rounded-lg border border-dashed ${borders.default}
                 flex items-center gap-3 py-2 px-3`}
    >
      <ItemBadge item={item} />
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className={`text-[11px] font-medium ${textColors.secondary} truncate w-full`}>
          {typeLabel(item.type)}
        </span>
        <span className={`text-[9px] ${textColors.muted}`}>
          Hidden — not supported in this mode
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main field component
// ---------------------------------------------------------------------------

export default function ComponentSlotManagerField({ allValues }) {
  const modeDef = allValues?.active_component_mode_definition;
  const layoutBuckets = allValues?.automatic_layout_buckets;
  const internalElements = allValues?.internal_elements;
  const componentId = allValues?.id;
  const { openAddModal } = useAddElement();
  const editContext = useContext(EditContext);
  const refreshElement = editContext?.refreshElement;

  const [reordering, setReordering] = useState(false);

  const totalElements = useMemo(
    () => (internalElements?.length ?? 0),
    [internalElements],
  );

  const handleAddElement = useCallback(
    (e) => {
      e.stopPropagation();
      openAddModal({ componentId, order: totalElements }, e);
    },
    [openAddModal, componentId, totalElements],
  );

  const sortedChildren = useMemo(
    () =>
      [...(internalElements || [])]
        .filter((el) => el && el.id != null && el.type)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [internalElements],
  );

  const unassigned = layoutBuckets?.unassigned || [];

  const unassignedKeySet = useMemo(() => {
    const set = new Set();
    for (const u of unassigned) {
      if (u?.id != null && u?.type) set.add(`${u.type}-${u.id}`);
    }
    return set;
  }, [unassigned]);

  const assignedItems = useMemo(
    () => sortedChildren.filter((el) => !unassignedKeySet.has(`${el.type}-${el.id}`)),
    [sortedChildren, unassignedKeySet],
  );

  const enrichItem = useCallback(
    (item) => {
      if (!item || !internalElements) return item;
      const match = internalElements.find(
        (el) => el.id === item.id && el.type === item.type,
      );
      return match ? { ...item, ...match } : item;
    },
    [internalElements],
  );

  const enrichedUnassigned = useMemo(
    () => unassigned.map(enrichItem).filter(Boolean),
    [unassigned, enrichItem],
  );

  const handleSwapItems = useCallback(
    async (itemA, itemB) => {
      if (!componentId || reordering || !itemA || !itemB) return;

      const ordered = [...sortedChildren];
      const idxA = ordered.findIndex((el) => el.id === itemA.id && el.type === itemA.type);
      const idxB = ordered.findIndex((el) => el.id === itemB.id && el.type === itemB.type);
      if (idxA < 0 || idxB < 0 || idxA === idxB) return;

      [ordered[idxA], ordered[idxB]] = [ordered[idxB], ordered[idxA]];

      const payload = {
        children: ordered.map((el) => ({ type: el.type, id: el.id })),
      };

      setReordering(true);
      try {
        const response = await apiFetch(
          `/api/components/${componentId}/reorder-children/`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          },
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        await response.json().catch(() => null);
        if (refreshElement) {
          await refreshElement(componentId, "component");
        }
      } catch (err) {
        console.error("[ComponentSlotManagerField] Reorder failed:", err);
      } finally {
        setReordering(false);
      }
    },
    [componentId, sortedChildren, refreshElement, reordering],
  );

  if (!modeDef || !layoutBuckets) return null;

  return (
    <div className="mt-4">
      <div className="mb-2">
        <label className={`${typography.label} ${textColors.secondary}`}>
          Slot Assignments
        </label>
      </div>

      <FlatItemList
        items={assignedItems}
        componentId={componentId}
        onSwapItems={handleSwapItems}
        reordering={reordering}
      />

      <button
        type="button"
        onClick={handleAddElement}
        className={`w-full mt-2 rounded-lg border border-dashed border-gray-300/50 dark:border-gray-600/40
                   flex items-center justify-center gap-2 py-2.5 px-3
                   text-[11px] font-medium
                   text-gray-500 dark:text-gray-400
                   hover:border-blue-400/60 hover:text-blue-500
                   dark:hover:border-blue-500/40 dark:hover:text-blue-400
                   transition-colors cursor-pointer bg-transparent`}
        title="Add a new element to this component"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span>Add Element</span>
      </button>

      {enrichedUnassigned.length > 0 && (
        <div className="mt-3">
          <label className={`block mb-1.5 ${typography.label} ${textColors.muted}`}>
            Hidden Items ({enrichedUnassigned.length})
          </label>
          <div className="space-y-1">
            {enrichedUnassigned.map((item) => (
              <UnassignedItemCard
                key={`${item.type}-${item.id}`}
                item={item}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
